import React, { useMemo, useCallback, useRef, useImperativeHandle, forwardRef } from 'react';
import {
  CellLeftV8,
  order2Sorting,
  toastSuccess,
  toastFailure,
  promiseModalDialog,
  TableDataState,
  EditCellV8,
  TableCellV8,
  TablePaginatedV8,
} from 'motion-components';
import { useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';

import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import {
  firstDateUserColumn,
  isActiveColumn,
  lastDateUserColumn,
  gridInactiveRowStyle,
  gridRowSelectedStyle,
} from 'shared-pages/common/grid-helpfunc';
import { useMutation } from '@apollo/client';
import { FormInput, gqlAdd, gqlDelete, gqlUpdateCollectorsRegions } from './graphql';
import * as Lo from 'lodash';
import { useGetRegionAsOptionsQuery } from 'shared-pages/components/field-select/fs-region';
import { useHandleError } from 'shared-pages/services';
import { isAfisCyprus, regionTitle } from 'shared-pages/common/constants';
/* ----------- Grid ------------------ */

export const CollectorRegionsDataGrid = forwardRef((props: any, ref) => {
  const {
    data,
    initPageIndex,
    onPageChange,
    loading,
    handleExport,
    initOrderBy,
    onOrderChange,
    selected,
    setSelected,
    refetch,
    dataCount,
    injectFooterElement,
  } = props;
  const { handleGraphqlError } = useHandleError('collectors-regions');
  const location = useLocation<any>();
  const history = useHistory();
  const { appStore, tableStore } = useRootStore();
  const { t, i18n } = useTranslation();
  const [updateCollectorsRegions] = useMutation(gqlUpdateCollectorsRegions);
  const [addNewRegion] = useMutation(gqlAdd);
  const [deleteRegion] = useMutation(gqlDelete);

  const columns = useMemo(
    () => [
      {
        id: 'id',
        header: t('ID'),
        columnName: t('ID'),
        accessorKey: 'id',
        orderByField: 'id',
        cell: CellLeftV8,
        size: 5,
      },

      {
        id: 'region.name',
        header: t(regionTitle),
        columnName: t(regionTitle),
        accessorKey: 'region.name',
        orderByField: 'region.name',
        cell: CellLeftV8,
        size: 50,
        /* meta: {
          type: 'select',
          hook: useGetRegionAsOptionsQuery,
          //where: regionsWhere,
          idKey: 'regionId',
          idName: 'region.name',
        }, */
      },
      {
        id: 'collector.name',
        header: t('Συλλέκτης'),
        columnName: t('Συλλέκτης'),
        accessorKey: 'collector.name',
        orderByField: 'collector.name',
        cell: CellLeftV8,
        size: 50,
        /* meta: {
          type: 'select',
          hook: useGetCollectorAsOptionsQuery,
          where: collectorsWhere,
          idKey: 'collectorId',
          idName: 'collector.name',
        }, */
      },
      isActiveColumn(t, i18n),
      firstDateUserColumn(t),
      lastDateUserColumn(t),
      ...(appStore.isPlatformOwner
        ? [
            {
              id: 'edit',
              header: t('Ενέργειες'),
              columnName: t('Ενέργειες'),
              accessorKey: 'edit',
              orderByField: 'lastUser',
              size: 120,
              cell: ({ row, table }) => {
                return true ? EditCellV8({ row, table }) : <>-</>;
              },
            },
          ]
        : []),
    ],
    [t],
  );

  const tableRef = useRef(null);

  // return of useRef
  useImperativeHandle(ref, () => (tableRef.current ? tableRef.current : null));

  return (
    <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
      {data ? (
        <TablePaginatedV8
          ref={tableRef}
          loading={loading}
          id="grid"
          appStore={appStore}
          tableStore={tableStore}
          key="grid"
          columns={columns as any}
          data={data}
          dataCount={dataCount}
          onRowClick={(table) => setSelected(table.row.original)}
          rowProps={(row: any) => {
            let style = undefined;
            if (row.original.id === selected?.id) style = gridRowSelectedStyle;
            //if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };
            return { style: style };
          }}
          onExport={handleExport}
          injectFooterElement={injectFooterElement}
          onPageChange={onPageChange}
          initPageIndex={initPageIndex}
          initSorting={order2Sorting(initOrderBy)}
          onSortByChange={(sort, columnDef, orderBy) => {
            onOrderChange(orderBy);
          }}
          onSaveRow={async (rowIndex, row, changes) => {
            //
            // i.e. Save Record
            //
            let id = row.id;
            const values = { id: id, ...changes };
            const mutValues = Lo.pick(values, Lo.keys(new FormInput()));
            // [α] Insert New Record
            if (tableStore.state === TableDataState.insert) {
              const res = await addNewRegion({
                variables: { data: { ...mutValues, collectorId: props.collectorId } },
              }).catch((reason) => {
                handleGraphqlError('mutation-insert', reason);
                //console.log('reason ', reason);
                toastFailure(reason);
                //console.error('$$$ grid.UpdateRow insert error reason', reason);
              });
              if (!res) return false; // some error returned
              id = res.data.addCollectorsRegion.id;
            }
            // ή [β] Update Existing Record
            else if (tableStore.state === TableDataState.edit) {
              const mutValues2 = {
                ...mutValues,
                // collectorId: row.collectorId, // Το χρησιμοποιεί στο Validation το Api
                // regionId: row.regionId,
              };
              const res = await updateCollectorsRegions({
                variables: { data: mutValues2 },
              }).catch((reason) => {
                console.error('$$$ grid.UpdateRow error reason', reason);
              });
              if (!res) return false; // some error returned
            }
            refetch();
            return id;
          }}
          deleteRow={async (row) => {
            // mark as deleted
            const ans = await promiseModalDialog({
              title: 'Διαγραφή Εγγραφής',
              text: `<div>Θέλετε να διαγράψετε τη Γεωγραφική Ενότητα <strong>${row.region.name}</strong>;</div>`,
            });
            if (!ans) return;
            const res = await deleteRegion({ variables: { id: row.id } }).catch((reason) => {
              console.error('$$$ grid.Delete error reason', reason);
            });
            if (!res) return false; // some error returned
            refetch();
            toastSuccess('Διαγράφηκε η Γεωγραφική Ενότητα');
            return true;
          }}
          onNewRow={() => {
            return {
              id: -1,
              //collectorId: props.collectorId,
              isActive: true,
            };
          }}
          topBorderRadius={false}
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </LoadingOverlay>
  );
});
